import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { CardElement } from '@stripe/react-stripe-js';
import { paymentMethodSetupAPI } from '../../services/api';
import LinkDisabledCardElement from '../../components/payments/LinkDisabledCardElement';
import LoadingSpinner from '../../components/LoadingSpinner';

function CardForm({
  token,
  clientSecret,
  done,
}: {
  token: string;
  clientSecret: string;
  done: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy || !stripe || !elements) return;
        setBusy(true);
        setError('');
        try {
          if (!confirmed) {
            const card = elements.getElement(CardElement);
            if (!card) throw new Error('Secure card entry is not ready.');
            const result = await stripe.confirmCardSetup(clientSecret, {
              payment_method: { card },
            });
            if (result.error)
              throw new Error(
                result.error.message || 'Card could not be saved.'
              );
            if (result.setupIntent?.status !== 'succeeded')
              throw new Error(
                'Complete the requested verification before continuing.'
              );
            setConfirmed(true);
          }
          const completion = await paymentMethodSetupAPI.complete(token);
          if (completion.data.status !== 'succeeded')
            throw new Error(
              'Card setup requires reconciliation. Contact the operator.'
            );
          done();
        } catch (failure: any) {
          setError(
            failure?.response?.data?.message ||
              failure.message ||
              'Card setup could not be completed.'
          );
        } finally {
          setBusy(false);
        }
      }}
      className="mt-5 space-y-4"
    >
      {!confirmed && (
        <div className="rounded-md border border-outline bg-white p-4">
          <LinkDisabledCardElement
            options={{
              style: { base: { color: '#111827', fontSize: '16px' } },
            }}
          />
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || !stripe}
        className="btn-primary w-full"
      >
        {busy
          ? 'Saving…'
          : confirmed
          ? 'Finish saving card'
          : 'Save card securely'}
      </button>
    </form>
  );
}

function ConnectedForm({
  config,
  token,
  done,
  progress,
}: {
  config: any;
  token: string;
  done: () => void;
  progress: (value: any) => void;
}) {
  const stripe = useMemo(
    () =>
      loadStripe(config.publishableKey, {
        stripeAccount: config.connectedAccountId,
      }),
    [config.publishableKey, config.connectedAccountId]
  );
  return (
    <Elements
      stripe={stripe}
      options={
        config.methodType === 'us_bank_account'
          ? { clientSecret: config.clientSecret }
          : undefined
      }
    >
      {config.methodType === 'us_bank_account' ? (
        <BankForm token={token} done={progress} />
      ) : (
        <CardForm
          token={token}
          clientSecret={config.clientSecret}
          done={done}
        />
      )}
    </Elements>
  );
}

function BankForm({
  token,
  done,
}: {
  token: string;
  done: (value: any) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  return (
    <form
      className="mt-5 space-y-4"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!stripe || !elements || busy || !ready || loadFailed) return;
        setBusy(true);
        setError('');
        try {
          if (!confirmed) {
            const result = await stripe.confirmSetup({
              elements,
              confirmParams: {
                return_url: `${window.location.origin}/payment-method/setup/${token}`,
              },
              redirect: 'if_required',
            });
            if (result.error)
              throw new Error(
                result.error.message || 'Bank setup could not be confirmed.'
              );
            if (
              !['succeeded', 'requires_action', 'processing'].includes(
                result.setupIntent?.status || ''
              )
            )
              throw new Error(
                'Complete the bank authorization before continuing.'
              );
            setConfirmed(true);
          }
          done((await paymentMethodSetupAPI.complete(token)).data);
        } catch (failure: any) {
          setError(
            failure?.response?.data?.message ||
              failure.message ||
              'Bank setup could not be completed.'
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      {!confirmed && (
        <PaymentElement
          onReady={() => setReady(true)}
          onLoadError={() => {
            setLoadFailed(true);
            setError(
              'Secure bank entry could not load. Reload this page and retry; no payment was taken.'
            );
          }}
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['us_bank_account'],
            wallets: { applePay: 'never', googlePay: 'never' },
          }}
        />
      )}
      {error && (
        <p role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
      <button
        className="btn-primary w-full"
        type="submit"
        disabled={busy || !stripe || !ready || loadFailed}
      >
        {!ready && !loadFailed
          ? 'Loading secure bank entry…'
          : busy
          ? 'Submitting…'
          : confirmed
          ? 'Finish bank setup'
          : 'Authorize bank setup'}
      </button>
    </form>
  );
}

export default function PaymentMethodSetupPage() {
  const { token = '' } = useParams();
  const query = useQuery(
    ['payment-method-setup', token],
    () =>
      paymentMethodSetupAPI.summary(token).then((response) => response.data),
    { retry: false }
  );
  const [consent, setConsent] = useState(false);
  const [setDefault, setSetDefault] = useState(false);
  const [config, setConfig] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState<any>(null);
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
  const data = query.data;
  const bank = data?.methodType === 'us_bank_account';
  const completed =
    done || data?.status === 'succeeded' || progress?.status === 'succeeded';
  const pending = ['awaiting_verification', 'processing'].includes(
    progress?.status || data?.status
  );
  const verificationUrl = progress?.verificationUrl || data?.verificationUrl;
  const unavailable =
    data?.expired ||
    (!data?.ready && !pending) ||
    ['cancelled', 'cancelling'].includes(data?.status);
  return (
    <main className="min-h-screen bg-surface px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-xl">
        <p className="text-sm font-semibold text-primary">
          {data?.operatorName || 'Secure card setup'}
        </p>
        <h1 className="mt-2 text-2xl font-semibold text-on-surface">
          {completed
            ? bank
              ? 'Bank account saved'
              : 'Card saved'
            : pending
            ? 'Bank verification pending'
            : 'Add a payment method'}
        </h1>
        {query.isLoading ? (
          <div className="mt-8">
            <LoadingSpinner />
          </div>
        ) : query.isError ? (
          <p role="alert" className="mt-4 text-sm">
            {(query.error as any)?.response?.data?.message ||
              'This setup link is unavailable.'}
          </p>
        ) : completed ? (
          <div className="mt-5">
            <p>
              Your {bank ? 'bank account' : 'card'} is securely saved with
              Stripe for this operator. No payment was taken.
            </p>
            <a
              href="/customer"
              className="mt-5 inline-block text-primary underline"
            >
              Go to Customer Action Center
            </a>
          </div>
        ) : unavailable ? (
          <p className="mt-5" role="status">
            {data?.expired
              ? 'This link expired. Request a new card setup link.'
              : 'This setup is unavailable. Contact the operator.'}
          </p>
        ) : pending ? (
          <div className="mt-5 space-y-4" role="status">
            <p>
              Your bank account is not ready for payments yet. Verification
              deposits can take 1–2 business days. Follow Stripe's verification
              instructions, then check the status here. No payment was taken.
            </p>
            {verificationUrl && (
              <a
                className="btn-primary inline-block"
                href={verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Verify bank account with Stripe
              </a>
            )}
            <button
              type="button"
              className="block underline"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError('');
                try {
                  setProgress(
                    (await paymentMethodSetupAPI.complete(token)).data
                  );
                } catch (failure: any) {
                  setError(
                    failure?.response?.data?.message ||
                      'Verification status could not be checked.'
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              Check verification status
            </button>
            {error && (
              <p role="alert" className="text-sm text-error">
                {error}
              </p>
            )}
            <a href="/customer" className="block text-primary underline">
              Go to Customer Action Center
            </a>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm text-on-surface-variant">
              Save a {bank ? 'US bank account' : 'card'} for future payments
              with {data.operatorName}. No payment will be taken now.
            </p>
            {!config ? (
              <form
                className="mt-6 space-y-4"
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (!consent || busy) return;
                  setBusy(true);
                  setError('');
                  try {
                    const response = await paymentMethodSetupAPI.begin(token, {
                      consent,
                      setDefault,
                      consentVersion: data.consentVersion,
                    });
                    if (response.data.status === 'succeeded') setDone(true);
                    else if (
                      ['awaiting_verification', 'processing'].includes(
                        response.data.status
                      )
                    )
                      setProgress(response.data);
                    else setConfig(response.data);
                  } catch (failure: any) {
                    setError(
                      failure?.response?.data?.message ||
                        'Secure card setup is unavailable. Retry this link.'
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <label className="flex items-start gap-3 text-sm leading-6">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 shrink-0"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                  />
                  {data.consentText}
                </label>
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={setDefault}
                    onChange={(event) => setSetDefault(event.target.checked)}
                  />
                  Make this my default payment method
                </label>
                {error && (
                  <p role="alert" className="text-sm text-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={!consent || busy}
                  className="btn-primary w-full"
                >
                  {busy
                    ? 'Preparing…'
                    : bank
                    ? 'Continue to secure bank entry'
                    : 'Continue to secure card entry'}
                </button>
              </form>
            ) : (
              <ConnectedForm
                token={token}
                config={config}
                done={() => setDone(true)}
                progress={setProgress}
              />
            )}
            <p className="mt-5 text-xs leading-5 text-on-surface-variant">
              {bank
                ? 'Bank details are entered directly into Stripe. Do not send account or routing numbers by email or chat. Bank verification is not payment settlement.'
                : 'Card details are entered directly into Stripe. Do not send card numbers or security codes by email or chat.'}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
